import { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../../../modal';
import { Button } from '../../../button';
import {
  IFindCarSaleQuery,
  useUpdateCarSaleMutation,
} from '../../../../services/carSaleServices/__generated__/queries.generated';
import { FIND_CAR_SALE } from '../../../../services/carSaleServices/queries';
import { toast } from 'react-toastify';
import { IPaymentScheduleInput, PaymentType } from '../../../../../__generated__/graphql';

const DisclosureTable = styled.table`
  ${tw`w-full border border-gray-300 border-solid relative`}
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  ${tw`border border-gray-600 bg-gray-100 p-2 text-sm font-bold text-center border-solid`}
`;

const TableCell = styled.td`
  ${tw`border border-gray-600 p-2 text-sm border-solid`}
`;

const Input = styled.input`
  ${tw`w-full border-none focus:outline-none text-sm text-center`}
`;

const WarningText = styled.div`
  ${tw`text-xs p-2 space-y-1`}
`;

const EstimateNote = styled.span`
  ${tw`text-xs italic`}
`;

const EditingContainer = styled.div`
  ${tw`flex flex-col items-center w-full min-w-[768px] gap-4 p-4`}
`;

const InputRow = styled.div`
  ${tw`grid grid-cols-3 gap-4 w-full`}
`;

const InputGroup = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const InputLabel = styled.label`
  ${tw`text-sm font-semibold`}
`;

const StyledInput = styled.input`
  ${tw`border border-gray-300 rounded px-3 py-2`}
`;

const SelectInput = styled.select`
  ${tw`border border-gray-300 rounded px-3 py-2`}
`;

const formatDateForInput = (dateString: string | null | undefined) => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().split('T')[0];
};

const formatDateForSubmission = (dateString: string) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toISOString();
};

const FederalDisclosuresComponent = ({
  agreementId,
  agreementData,
}: {
  agreementId: string;
  agreementData: IFindCarSaleQuery | undefined;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>({
    paymentType: agreementData?.carSale?.paymentSchedule?.paymentType || PaymentType.Cash,
    startDate: formatDateForInput(agreementData?.carSale?.paymentSchedule?.startDate) || '',
    annualPercentageRate: agreementData?.carSale?.paymentSchedule?.annualPercentageRate || 0,
    financeCharge: agreementData?.carSale?.paymentSchedule?.financeCharge || 0,
    amountFinanced: agreementData?.carSale?.paymentSchedule?.amountFinanced || 0,
    totalOfPayments: agreementData?.carSale?.paymentSchedule?.totalOfPayments || 0,
    totalSalePrice: agreementData?.carSale?.paymentSchedule?.totalSalePrice || 0,
    firstPaymentDate: formatDateForInput(agreementData?.carSale?.paymentSchedule?.firstPaymentDate),
    finalPaymentDate: formatDateForInput(agreementData?.carSale?.paymentSchedule?.finalPaymentDate),
    monthlyPaymentAmount: agreementData?.carSale?.paymentSchedule?.monthlyPaymentAmount || 0,
    finalPaymentAmount: agreementData?.carSale?.paymentSchedule?.finalPaymentAmount || 0,
    hasSecurityInterest: agreementData?.carSale?.paymentSchedule?.hasSecurityInterest || false,
    lateChargeRate: agreementData?.carSale?.paymentSchedule?.lateChargeRate || 5,
  });

  const updatePaymentInfo = (data: any) => {
    if (data) {
      setPaymentInfo({
        paymentType: data.paymentType || PaymentType.Cash,
        startDate: formatDateForInput(data.startDate),
        annualPercentageRate: data.annualPercentageRate || 0,
        financeCharge: data.financeCharge || 0,
        amountFinanced: data.amountFinanced || 0,
        totalOfPayments: data.totalOfPayments || 0,
        totalSalePrice: data.totalSalePrice || 0,
        firstPaymentDate: formatDateForInput(data.firstPaymentDate),
        finalPaymentDate: formatDateForInput(data.finalPaymentDate),
        monthlyPaymentAmount: data.monthlyPaymentAmount || 0,
        finalPaymentAmount: data.finalPaymentAmount || 0,
        hasSecurityInterest: data.hasSecurityInterest || false,
        lateChargeRate: data.lateChargeRate || 5,
      });
    }
  };

  useEffect(() => {
    if (agreementData?.carSale?.paymentSchedule) {
      updatePaymentInfo(agreementData.carSale.paymentSchedule);
    }
  }, [agreementData]);

  const [UPDATE_CAR_SALE] = useUpdateCarSaleMutation({
    refetchQueries: [FIND_CAR_SALE],
    onCompleted: data => {
      setIsEditing(false);
      updatePaymentInfo(data.updateCarSale.paymentSchedule);
      toast.success('Payment schedule updated successfully');
    },
    onError: error => {
      toast.error('Error updating payment schedule');
    },
  });

  const handleSave = async () => {
    if (!agreementId) return;

    const formattedPaymentInfo = {
      ...paymentInfo,
      startDate: formatDateForSubmission(paymentInfo?.startDate as unknown as string),
      firstPaymentDate: paymentInfo?.firstPaymentDate || null,
      finalPaymentDate: paymentInfo?.finalPaymentDate || null,
    };

    await UPDATE_CAR_SALE({
      variables: {
        input: {
          id: agreementId,
          paymentSchedule: formattedPaymentInfo as IPaymentScheduleInput,
        },
      },
    });
  };

  const renderPaymentType = () => {
    switch (paymentInfo?.paymentType) {
      case PaymentType.Cash:
        return 'COD';
      case PaymentType.Finance:
        return 'Monthly';
      case PaymentType.Lease:
        return 'Monthly';
      default:
        return 'Monthly';
    }
  };

  const renderEditModal = () => (
    <Modal isOpen={isEditing} onClose={() => setIsEditing(false)}>
      <EditingContainer>
        <InputRow>
          <InputGroup>
            <InputLabel>Payment Type</InputLabel>
            <SelectInput
              value={paymentInfo?.paymentType || ''}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  paymentType: e.target.value,
                })
              }
            >
              <option value={PaymentType.Cash}>Cash</option>
              <option value={PaymentType.Finance}>Finance</option>
              <option value={PaymentType.Lease}>Lease</option>
            </SelectInput>
          </InputGroup>

          <InputGroup>
            <InputLabel>Start Date</InputLabel>
            <StyledInput
              type="date"
              value={paymentInfo.startDate}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  startDate: e.target.value,
                })
              }
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>Annual Percentage Rate (%)</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentInfo.annualPercentageRate}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  annualPercentageRate: parseFloat(e.target.value || '0'),
                })
              }
            />
          </InputGroup>
        </InputRow>

        <InputRow>
          <InputGroup>
            <InputLabel>Finance Charge ($)</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentInfo.financeCharge}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  financeCharge: parseFloat(e.target.value || '0'),
                })
              }
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>Amount Financed ($)</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentInfo.amountFinanced}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  amountFinanced: parseFloat(e.target.value || '0'),
                })
              }
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>Total of Payments ($)</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentInfo.totalOfPayments}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  totalOfPayments: parseFloat(e.target.value || '0'),
                })
              }
            />
          </InputGroup>
        </InputRow>

        <InputRow>
          <InputGroup>
            <InputLabel>Total Sale Price ($)</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentInfo.totalSalePrice}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  totalSalePrice: parseFloat(e.target.value || '0'),
                })
              }
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>First Payment Date</InputLabel>
            <StyledInput
              type="date"
              value={paymentInfo.firstPaymentDate}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  firstPaymentDate: e.target.value,
                })
              }
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>Final Payment Date</InputLabel>
            <StyledInput
              type="date"
              value={paymentInfo.finalPaymentDate}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  finalPaymentDate: e.target.value,
                })
              }
            />
          </InputGroup>
        </InputRow>

        <InputRow>
          <InputGroup>
            <InputLabel>Monthly Payment Amount ($)</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentInfo.monthlyPaymentAmount}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  monthlyPaymentAmount: parseFloat(e.target.value || '0'),
                })
              }
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>Final Payment Amount ($)</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentInfo.finalPaymentAmount}
              onChange={e =>
                setPaymentInfo({
                  ...paymentInfo,
                  finalPaymentAmount: parseFloat(e.target.value || '0'),
                })
              }
            />
          </InputGroup>
        </InputRow>

        <Button text="Save" onClick={handleSave} />
      </EditingContainer>
    </Modal>
  );

  return (
    <>
      <DisclosureTable onClick={() => setIsEditing(true)}>
        <thead>
          <tr>
            <TableHeader>ANNUAL PERCENTAGE RATE</TableHeader>
            <TableHeader>FINANCE CHARGE</TableHeader>
            <TableHeader>Amount Financed</TableHeader>
            <TableHeader>Total of Payments</TableHeader>
            <TableHeader>Total Sale Price</TableHeader>
          </tr>
        </thead>
        <tbody>
          <tr>
            <TableCell className="text-center">The cost of your credit as a yearly rate.</TableCell>
            <TableCell className="text-center">
              The dollar amount the credit will cost you.
            </TableCell>
            <TableCell className="text-center">
              The amount of credit provided to you or on your behalf.
            </TableCell>
            <TableCell className="text-center">
              The amount you will have paid after making all payments as scheduled.
            </TableCell>
            <TableCell className="text-center">
              The total cost of your purchase on credit, including your down payment of $0.00
            </TableCell>
          </tr>
        </tbody>
        <tbody>
          <tr>
            <TableCell className="text-center">{paymentInfo?.annualPercentageRate}%</TableCell>
            <TableCell className="text-center">{paymentInfo?.financeCharge}</TableCell>
            <TableCell className="text-center">{paymentInfo?.amountFinanced}</TableCell>
            <TableCell className="text-center">{paymentInfo?.totalOfPayments}</TableCell>
            <TableCell className="text-center">{paymentInfo?.totalSalePrice}</TableCell>
          </tr>

          <tr>
            <TableCell colSpan={5} className="text-center text-xs italic">
              (e) means an estimate
            </TableCell>
          </tr>

          <tr>
            <TableHeader colSpan={5}>YOUR PAYMENT SCHEDULE WILL BE:</TableHeader>
          </tr>

          <tr>
            <TableCell>Number of Payments</TableCell>
            <TableCell>Amount of Payments</TableCell>
            <TableCell colSpan={3}>When Payments Are Due</TableCell>
          </tr>

          <tr>
            <TableCell>One Payment of</TableCell>
            <TableCell>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
            <TableCell colSpan={3}>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
          </tr>

          <tr>
            <TableCell>One Payment of</TableCell>
            <TableCell>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
            <TableCell colSpan={3}>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
          </tr>

          <tr>
            <TableCell>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
            <TableCell>
              <Input type="text" defaultValue={paymentInfo?.totalSalePrice || ''} />
            </TableCell>
            <TableCell colSpan={3}>
              <div className="flex items-center">
                <div className="font-bold mr-2">{renderPaymentType()}</div>
                <Input type="text" defaultValue={paymentInfo?.firstPaymentDate || ''} />
                <span className="ml-2">beginning</span>
              </div>
            </TableCell>
          </tr>

          <tr>
            <TableCell>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
            <TableCell>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
            <TableCell colSpan={3}>
              <Input type="text" defaultValue="N/A" />
            </TableCell>
          </tr>

          <tr>
            <TableCell>One final payment</TableCell>
            <TableCell>
              <Input type="text" defaultValue={paymentInfo?.finalPaymentAmount || ''} />
            </TableCell>
            <TableCell colSpan={3}>
              <Input type="text" defaultValue={paymentInfo?.finalPaymentDate || ''} />
            </TableCell>
          </tr>

          <tr>
            <TableCell colSpan={5}>
              <WarningText>
                <p>
                  <b>Late Charge.</b> If payment is not received in full within 10 days after it is
                  due, you will pay a late charge of 5% of the part of the payment that is late.
                </p>
                <p>
                  <b>Prepayment.</b> If you pay early, you may be charged a minimum finance charge.
                </p>
                <p>
                  <b>Security Interest.</b> You are giving a security interest in the vehicle being
                  purchased.
                </p>
                <p>
                  <b>Additional Information:</b> See this contract for more information including
                  information about nonpayment, default, any required repayment in full before the
                  scheduled date, minimum finance charges, and security interest.
                </p>
              </WarningText>
            </TableCell>
          </tr>
        </tbody>
      </DisclosureTable>
      {renderEditModal()}
    </>
  );
};

export default FederalDisclosuresComponent;
